import React from "react";

import { FiftyWaysCardItem } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ButtonTextPrimary } from "../Clickables";
import { H } from "../Headings";
import { RichText } from "../RichText";
import { Svg } from "../Svg";
import { getThemeClass } from "../ThemedModule";

import styles from "./FiftyWaysCards.module.scss";

interface CardProps {
    card: FiftyWaysCardItem;
    angleDeg: number;
    isFocused: boolean;
    isFlipped: boolean;
    isInert: boolean;
    onFlip: () => void;
    onNavigate?: () => void;
    zIndex: number;
}

export const Card = ({
    card,
    angleDeg,
    isFocused,
    isFlipped,
    isInert,
    onFlip,
    onNavigate,
    zIndex,
}: CardProps) => {
    const navigable = !isFocused && !!onNavigate;
    return (
        <div
            className={styles.cardSlot}
            style={{
                "--card-angle": `${angleDeg}deg`,
                zIndex,
            }}
            data-navigable={navigable || undefined}
            aria-hidden={!isFocused || undefined}
            inert={isInert || undefined}
            onClick={navigable ? onNavigate : undefined}
        >
            <div
                className={concatClassNames([
                    styles.flipContainer,
                    getThemeClass(card.card_theme),
                ])}
                data-flipped={isFlipped || undefined}
            >
                {/* Front */}
                <div
                    className={styles.front}
                    aria-hidden={isFlipped || undefined}
                    inert={isFlipped || undefined}
                >
                    <header className={styles.cardHeader}>
                        {card.eyebrow && (
                            <p className={styles.eyebrow}>{card.eyebrow}</p>
                        )}
                        <H className={styles.cardTitle}>{card.title}</H>
                        <div className={styles.divider} />
                    </header>
                    <div className={styles.cardBody}>
                        <p className={styles.frontBody}>{card.front_body}</p>
                    </div>
                    {card.cta_label && (
                        <div className={styles.ctaWrapper}>
                            <ButtonTextPrimary
                                onPress={onFlip}
                                isDisabled={!isFocused}
                            >
                                {card.cta_label}
                                <Svg name="arrow" />
                            </ButtonTextPrimary>
                        </div>
                    )}
                </div>

                {/* Back */}
                <div
                    className={styles.back}
                    aria-hidden={!isFlipped || undefined}
                    inert={!isFlipped || undefined}
                    onClick={onFlip}
                >
                    <header className={styles.backHeader}>
                        {card.eyebrow && (
                            <p className={styles.backEyebrow}>{card.eyebrow}</p>
                        )}
                        <H className={styles.backTitle}>{card.title}</H>
                        <div className={styles.backDivider} />
                    </header>
                    <div className={styles.backContent}>
                        <section className={styles.backSection}>
                            <p className={styles.backLabel}>
                                {card.why_it_matters_label}
                            </p>
                            <p className={styles.backBody}>
                                {card.why_it_matters_body}
                            </p>
                        </section>
                        <section className={styles.backSection}>
                            <p className={styles.backLabel}>
                                {card.what_to_do_label}
                            </p>
                            {card.what_to_do_body && (
                                <RichText
                                    className={styles.backRichText}
                                    value={card.what_to_do_body}
                                />
                            )}
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};
